import { useState } from "react";
import { Modal } from "bootstrap";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import API from "../../../services/api";
import { useNavigate } from "react-router-dom";

export default function useDBLeadActions({
  auth,
  form,
  setForm,
  resetForm,
  validate,
  setValidated,
  filters,
  perPage,
  setLeads,
  setChartData,
  setTotalPages,
  setTotalData,
  setTableLoading,
  setSubCategoryDisable,
  setProductTypeDisable,
  setGetSubCategoryLoading,
  setGetProductTypeLoading,
  setLookupSubCategory,
  setLookupTypeProduct,
  setLookupCategory,
  setLookupUsers,
  modalRef,
  importModalRef,
  fileInputRef,
}) {
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [editLoading, setEditLoading] = useState(false);

  const [editId, setEditId] = useState(null);
  const navigate = useNavigate();

  const fetchDBLeads = async (
    page = 1,
    filterParams = {},
    perPageSize = perPage
  ) => {
    setTableLoading(true);
    try {
      const res = await API.get("/database-leads", {
        params: { page, per_page: perPageSize, ...filterParams },
      });
      setLeads(res.data.data);
      setTotalPages(res.data.meta.last_page);
      setTotalData(res.data.meta.total);
      setChartData(res.data.chart);
    } catch (err) {
      toast.error("Sesi habis. Silakan login ulang.");
      navigate("/login");
    } finally {
      setTableLoading(false);
    }
  };

  const fetchLookupCategory = async () => {
    
    try {
      let res;
      if (auth?.mo?.id) {
        res = await API.get(`/lookup/${auth.mo.id}/getCategoriesByMo`);
      } else {
        res = await API.get(`/lookup/all/getAllMoCategories`);
      }
      setLookupCategory(res.data.mo);
    } catch {
      toast.error("Kategori tidak bisa diakses.");
    }
  };

  const fetchLookupUsers = async (moId = null) => {
    try {
      const res = await API.get(`/lookup/users`, {
        params: moId ? { moId } : {},
      });
      setLookupUsers(res.data);
    } catch {
      toast.error("Data pengguna tidak bisa diakses.");
      navigate("/login");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formEl = e.currentTarget;
    const html5Valid = formEl.checkValidity();
    const customValid = validate();

    if (!html5Valid || !customValid) {
      e.stopPropagation();
      setValidated(true);
      return;
    }

    setSubmitLoading(true);
    try {
      if (editId) {
        await API.put(`/database-leads/${editId}`, form);
        toast.info("Lead berhasil diperbarui.");
      } else {
        await API.post("/database-leads", form);
        toast.success("Lead berhasil ditambah.");
      }

      resetForm();
      setEditId(null);
      fetchDBLeads(1, filters);

      setSubCategoryDisable(true);
      setProductTypeDisable(true);
      setLookupSubCategory([]);
      setLookupTypeProduct([]);

      const modalInstance =
        Modal.getInstance(modalRef.current) || new Modal(modalRef.current);
      modalInstance.hide();
      document.body.classList.remove("modal-open");
      document.querySelectorAll(".modal-backdrop").forEach((el) => el.remove());

      setValidated(false);
    } catch (err) {
      const errorMessage =
        err.response?.data?.message || "Gagal menyimpan lead!";
      toast.error(errorMessage);
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleEdit = async (lead) => {
    setEditLoading(true);
    const clone = structuredClone(lead);

    try {
      const productCategoryId = clone.product_category?.id ?? "";
      const productSubcategoryId = clone.product_subcategory?.id ?? "";

      if (productCategoryId) {
        const resSub = await API.get(
          `/lookup/${productCategoryId}/getSubCategories`
        );
        setLookupSubCategory(resSub.data.subCategories);
        setSubCategoryDisable(false);
      }
      if (productSubcategoryId) {
        const resType = await API.get(
          `/lookup/${productSubcategoryId}/getProductTypes`
        );
        setLookupTypeProduct(resType.data.productTypes);
        setProductTypeDisable(false);
      }

      const { created_by, updated_by, ...rest } = clone;

      setForm({
        ...rest,
        phone_number: clone.phone_number ? atob(clone.phone_number) : "",
        product_category: productCategoryId,
        product_subcategory: productSubcategoryId,
        product_type: clone.product_type?.id ?? "",
        mo_id: clone.mo_id?.map((m) => m.id) ?? [],
        tls_id: clone.tls_id?.map((t) => t.id) ?? [],
        sales_id: clone.sales_id?.map((s) => s.id) ?? [],
      });

      setEditId(clone.id);
      new Modal(modalRef.current).show();
    } catch {
      toast.error("Gagal memuat data edit");
    } finally {
      setEditLoading(false);
    }
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Yakin?",
      text: "Lead akan dihapus secara permanen.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Ya, hapus",
      cancelButtonText: "Batal",
    });

    if (result.isConfirmed) {
      try {
        await API.delete(`/database-leads/${id}`);
        toast.success("Lead berhasil dihapus!");
        fetchDBLeads(1, filters);
      } catch {
        toast.error("Gagal menghapus lead.");
      }
    }
  };

  const handleExport = async () => {
    setExportLoading(true);
    try {
      const res = await API.get("/database-leads/export", {
        responseType: "blob",
        params: { ...filters },
      });

      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "database-leads.xlsx");
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error("Export gagal: " + error.message);
    } finally {
      setExportLoading(false);
    }
  };

  const handleImport = async (formData) => {
    try {
      setUploading(true);
      setUploadProgress(0);
      const res = await API.post("/database-leads/import", formData, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (event) => {
          const percent = Math.round((event.loaded * 100) / event.total);
          setUploadProgress(percent);
        },
      });

      const result = res?.data;
      toast.success(
        `✅ Import selesai!\n\n📥 Ditambahkan : ${result.inserted}\n❌ Gagal       : ${result.failed}`,
        { style: { whiteSpace: "pre-line" }, autoClose: false }
      );
      fetchDBLeads(1, filters);
      Modal.getInstance(importModalRef.current)?.hide();
    } catch (err) {
      toast.error(
        "Gagal import: " + (err.response?.data?.message || err.message)
      );
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = "";
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const onCategoryChange = async (e) => {
    const categoryId = e.target.value;
    setSubCategoryDisable(true);
    setProductTypeDisable(true);
    setGetSubCategoryLoading(true);

    setForm((prev) => ({
      ...prev,
      product_category: categoryId,
      product_subcategory: "",
      product_type: "",
    }));

    if (categoryId) {
      try {
        const res = await API.get(`/lookup/${categoryId}/getSubCategories`);
        setLookupSubCategory(res.data.subCategories);
        setSubCategoryDisable(false);
        setGetSubCategoryLoading(false);
        setLookupTypeProduct([]);
      } catch {
        setSubCategoryDisable(true);
        setGetSubCategoryLoading(false);
      }
    } else {
      setLookupSubCategory([]);
      setLookupTypeProduct([]);
    }
  };

  const onSubCategoryChange = async (e) => {
    const subCategoryId = e.target.value;
    setProductTypeDisable(true);
    setGetProductTypeLoading(true);

    setForm((prev) => ({
      ...prev,
      product_subcategory: subCategoryId,
      product_type: "",
    }));

    if (subCategoryId) {
      try {
        const res = await API.get(`/lookup/${subCategoryId}/getProductTypes`);
        setLookupTypeProduct(res.data.productTypes);
        setProductTypeDisable(false);
        setGetProductTypeLoading(false);
      } catch {
        setProductTypeDisable(true);
        setGetProductTypeLoading(false);
      }
    } else {
      setLookupTypeProduct([]);
    }
  };

  const handleAddModal = () => {
    setEditId(null);
    resetForm();

    setLookupSubCategory([]);
    setLookupTypeProduct([]);
    setSubCategoryDisable(true);
    setProductTypeDisable(true);
    setGetSubCategoryLoading(false);
    setGetProductTypeLoading(false);

    const modalInstance =
      Modal.getInstance(modalRef.current) || new Modal(modalRef.current);
    modalInstance.show();
  };

  return {
    onCategoryChange,
    onSubCategoryChange,
    fetchDBLeads,
    fetchLookupCategory,
    fetchLookupUsers,
    handleSubmit,
    handleEdit,
    handleDelete,
    handleExport,
    handleImport,
    handleAddModal,
    uploadProgress,
    uploading,
    submitLoading,
    exportLoading,
    editLoading,
    editId,
    setEditId,
  };
}
